import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Chip from '@material-ui/core/Chip'
import CircularProgress from '@material-ui/core/CircularProgress'
import Dialog from '@material-ui/core/Dialog'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import IconButton from '@material-ui/core/IconButton'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import ListItemText from '@material-ui/core/ListItemText'
import Typography from '@material-ui/core/Typography'
import { makeStyles } from '@material-ui/core/styles'
import { useLazyQuery } from '@apollo/client'
import { useSnackbar } from 'notistack'
import CloseIcon from 'components/material-symbol/icons/Close'
import RecurringChip from 'components/group-events/recurring-chip'
import pluralize from 'lib/pluralize'
import { parseEvent } from 'utils/eventUtils'
import EventDetailsDialog from '../event-details-dialog'
import CoachEventCard from './coach-event-card'
import { GET_GROUP_EVENT_DETAILS_BY_SLUG } from '../query'

const STACK_OFFSET = 6

const useStyles = makeStyles(theme => ({
  stack: {
    position: 'relative',
    height: '100%',
    paddingTop: STACK_OFFSET * 2,
  },
  layer: {
    position: 'absolute',
    height: `calc(100% - ${STACK_OFFSET * 2}px)`,
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.common.white,
  },
  layerBack: {
    top: 0,
    left: STACK_OFFSET * 2,
    right: STACK_OFFSET * 2,
    backgroundColor: '#EEF2FA',
  },
  layerMiddle: {
    top: STACK_OFFSET,
    left: STACK_OFFSET,
    right: STACK_OFFSET,
    backgroundColor: '#F6F8FC',
  },
  card: {
    position: 'relative',
    height: '100%',
  },
  summary: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
    borderRadius: 8,
    backgroundColor: '#F5F8FF',
  },
  summaryText: {
    fontSize: '0.8125rem',
    lineHeight: 1.3,
    color: theme.palette.text.primary,
  },
  viewDates: {
    flexShrink: 0,
    fontWeight: 600,
  },
  dialogTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
  },
  recurringChip: {
    marginTop: theme.spacing(1),
  },
  dateRow: {
    borderRadius: 8,
  },
  canceledChip: {
    backgroundColor: '#FFBCBC',
    color: '#410002',
    height: 20,
    fontSize: '0.625rem',
  },
  progress: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(2),
  },
}))

const registrantsLabel = occurrence => {
  const count = occurrence.registrationCount ?? 0
  return occurrence.maxAttendance
    ? `${count}/${occurrence.maxAttendance} registered`
    : `${count} registered`
}

export const summarizeOccurrences = occurrences => {
  const active = occurrences.filter(occurrence => !occurrence.isCanceled)
  return {
    dateCount: active.length,
    registrantCount: active.reduce(
      (total, occurrence) => total + (occurrence.registrationCount ?? 0),
      0,
    ),
  }
}

const RecurringDatesDialog = ({
  isOpen,
  onClose,
  title,
  occurrences,
  onSelect,
}) => {
  const classes = useStyles()

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Box>
          <Typography variant="h6">{title}</Typography>
          <RecurringChip className={classes.recurringChip} />
        </Box>
        <IconButton aria-label="Close" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Each date has its own registration. Select a date to see its roster.
        </Typography>
        <List disablePadding>
          {occurrences.map(occurrence => (
            <ListItem
              key={occurrence.id}
              button
              className={classes.dateRow}
              onClick={() => onSelect(occurrence)}
            >
              <ListItemText
                primary={occurrence.eventDayAndTime}
                secondary={registrantsLabel(occurrence)}
              />
              {occurrence.isCanceled ? (
                <Chip
                  label="Canceled"
                  size="small"
                  className={classes.canceledChip}
                />
              ) : null}
            </ListItem>
          ))}
        </List>
      </DialogContent>
    </Dialog>
  )
}

const RecurringEventCard = ({ event, occurrences, isPastTab, onPayment }) => {
  const classes = useStyles()
  const [isDatesOpen, setIsDatesOpen] = useState(false)
  const [selectedOccurrence, setSelectedOccurrence] = useState(null)
  const [detailView, setDetailView] = useState('detail')
  const { enqueueSnackbar } = useSnackbar()
  const [getEventDetails, { loading: isDetailsLoading }] = useLazyQuery(
    GET_GROUP_EVENT_DETAILS_BY_SLUG,
    {
      fetchPolicy: 'network-only',
      onCompleted: data => {
        const fetchedEvent = data?.currentUser?.groupEvents?.[0]
        if (fetchedEvent) {
          setSelectedOccurrence(parseEvent(fetchedEvent))
        }
      },
      onError: error => {
        enqueueSnackbar(
          error?.message || 'Unable to load event details. Please try again.',
          { variant: 'error' },
        )
      },
    },
  )
  const { dateCount, registrantCount } = summarizeOccurrences(occurrences)
  const dateLabel = `${dateCount} ${
    isPastTab ? 'past' : 'upcoming'
  } ${pluralize(dateCount, 'date')}`

  useEffect(() => {
    if (selectedOccurrence) {
      setDetailView('detail')
    }
  }, [selectedOccurrence])

  const handleSelectOccurrence = occurrence => {
    setIsDatesOpen(false)
    getEventDetails({ variables: { slug: occurrence.slug } })
  }

  const handleSetDetailView = view => {
    if (view) {
      setDetailView(view)
    } else {
      setSelectedOccurrence(null)
      setDetailView('detail')
    }
  }

  return (
    <Box className={classes.stack} data-testid="recurring-event-card">
      {dateCount > 2 ? (
        <Box className={`${classes.layer} ${classes.layerBack}`} />
      ) : null}
      {dateCount > 1 ? (
        <Box className={`${classes.layer} ${classes.layerMiddle}`} />
      ) : null}
      <Box className={classes.card}>
        <CoachEventCard
          event={event}
          onPayment={onPayment}
          extraChip={<RecurringChip />}
          sessionLabel={dateLabel}
          summary={
            <Box className={classes.summary}>
              <Typography className={classes.summaryText}>
                {registrantCount} {pluralize(registrantCount, 'registrant')}{' '}
                across {dateCount} {pluralize(dateCount, 'date')}
              </Typography>
              <Button
                size="small"
                color="primary"
                className={classes.viewDates}
                onClick={() => setIsDatesOpen(true)}
              >
                View dates
              </Button>
            </Box>
          }
        />
      </Box>
      <RecurringDatesDialog
        isOpen={isDatesOpen}
        onClose={() => setIsDatesOpen(false)}
        title={event.title}
        occurrences={occurrences}
        onSelect={handleSelectOccurrence}
      />
      {selectedOccurrence ? (
        <EventDetailsDialog
          isOpen={detailView}
          event={selectedOccurrence}
          setIsOpen={handleSetDetailView}
          onPayment={onPayment}
        />
      ) : null}
      {isDetailsLoading ? (
        <Box className={classes.progress}>
          <CircularProgress />
        </Box>
      ) : null}
    </Box>
  )
}

RecurringEventCard.propTypes = {
  event: PropTypes.object.isRequired,
  occurrences: PropTypes.array.isRequired,
  isPastTab: PropTypes.bool,
  onPayment: PropTypes.func,
}

export default RecurringEventCard
